import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import config from '../../config';
import { requireUserId } from './userSession';
import { usePrinterRouting } from './printerService';
import ProcessPaymentDialog from './ProcessPaymentDialog';
import PaymentLedgerModal from './PaymentLedgerModal';
import { buildLogoEscPos, buildUpiQrEscPos } from './escposLogo';
import { usePermission } from '../rbac/PermissionProvider';
import { P } from '../rbac/permissionCodes';

// Material UI Icons (All imports in one place - No duplicates)
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonIcon from '@mui/icons-material/Person';
import { MergeTableModal, SplitBillModal } from './TableMerge_BillSplit';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import CallSplitIcon from '@mui/icons-material/CallSplit';

import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Chip,
  Alert,
  Grid,
  Card,
  CardContent,
  IconButton,
  DialogContentText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  Drawer,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

const ORDER_DRAWER_WIDTH = { xs: '100%', sm: 420, md: 480 };
const ORDER_DRAWER_Z = 1400;

const drawerTheme = {
  primary: '#1565c0',
  bg: '#f0f4f8',
  surface: '#fff',
  border: '#e3eaf3',
  divider: '#e8edf3',
  textMuted: 'rgba(0,0,0,0.55)',
};

function DrawerSection({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          color: drawerTheme.primary,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          mb: 0.75,
        }}>
        {title}
      </Typography>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${drawerTheme.border}`,
          borderRadius: 2,
          bgcolor: drawerTheme.surface,
          overflow: 'hidden',
        }}>
        {children}
      </Paper>
    </Box>
  );
}

function DrawerRow({ label, value, highlight, isLast }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 1.75,
        py: 1.1,
        minHeight: 40,
        bgcolor: highlight ? '#f0f7ff' : 'transparent',
        borderBottom: isLast ? 'none' : `1px solid ${drawerTheme.divider}`,
      }}>
      <Typography
        sx={{ fontSize: 12.5, color: drawerTheme.textMuted, minWidth: 0 }}>
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: highlight ? 13.5 : 12.5,
          fontWeight: highlight ? 700 : 600,
          color: highlight ? drawerTheme.primary : 'text.primary',
          textAlign: 'right',
          flexShrink: 0,
        }}>
        {value}
      </Typography>
    </Box>
  );
}

export default function TableOrderList() {
  const { hasPermission: providerHasPermission } = usePermission();

  // ── FIXED: Read permissions from sessionStorage with direct string matching ──
  const getStoredPermissions = useCallback(() => {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem('permissionCodes') || '[]',
      );
      return Array.isArray(stored) ? stored : [];
    } catch (_) {
      return [];
    }
  }, []);

  const hasPermission = useCallback(
    (permission) => {
      const stored = getStoredPermissions();
      if (stored.length > 0) {
        // ── FIX: match both .code and .moduleKey to be safe ──
        return stored.some(
          (x) => x.code === permission || x.code?.trim() === permission?.trim(),
        );
      }
      return providerHasPermission(permission);
    },
    [providerHasPermission, getStoredPermissions],
  );

  const hasAnyPermission = useCallback(
    (codes) => {
      if (!codes?.length) return true;
      const stored = getStoredPermissions();
      if (stored.length > 0) {
        return codes.some((code) =>
          stored.some(
            (x) => x.code === code || x.code?.trim() === code?.trim(),
          ),
        );
      }
      return codes.some((c) => providerHasPermission(c));
    },
    [providerHasPermission, getStoredPermissions],
  );

  // ── FIXED: useGranularRbac — only true when permissionCodes array is non-empty ──
  const useGranularRbac = getStoredPermissions().length > 0;

  // ── FIXED: Permission checks using direct string literals as fallback ──
  // This ensures even if P.* constants have wrong values, direct strings work.
  const checkPerm = useCallback(
    (constantCode, literalCode) => {
      const stored = getStoredPermissions();
      if (stored.length === 0) return true; // no RBAC, allow all
      return stored.some(
        (x) =>
          x.code === constantCode ||
          x.code === literalCode ||
          x.code?.trim() === literalCode?.trim(),
      );
    },
    [getStoredPermissions],
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [searchText, setSearchText] = useState('');
  const [tableFilter, setTableFilter] = useState('');
  // Order TYPE tabs (All | Dine-In | Room). Pre-selected from the ?type= URL
  // param that CreateOrder appends after placing — so a room order lands on the
  // Room tab and a table/dine-in order on the Dine-In tab.
  const [typeFilter, setTypeFilter] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get('type');
      if (t === 'Room' || t === 'Dinein') return t;
    } catch (e) {
      /* ignore */
    }
    return 'Dinein';
  });
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState([]);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  // Manage actions dialog — the details drawer is now detail-only; all the
  // order actions live here, opened from the orders table.
  const [manageOpen, setManageOpen] = useState(false);
  const [ledgerOpen, setLedgerOpen] = useState(false);
  const [ledgerFor, setLedgerFor] = useState({ orderId: 0, bookingId: 0 });
  // Centralised printer routing (Department → mapped printer, + bill printer).
  const { resolveKotPrinter, resolveBillPrinter } = usePrinterRouting();

  // Modal States
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [currentOrderForAction, setCurrentOrderForAction] = useState(null);

  // Payment Modal States (the dialog itself owns method/amount/split state).
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [currentOrderForPayment, setCurrentOrderForPayment] = useState(null);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);
  const [splitModalOpen, setSplitModalOpen] = useState(false);
  const [orderForSplit, setOrderForSplit] = useState(null);

  // Restaurant-only company (propertType 'R') has no rooms, so "Bill to Room"
  // (room-service billing) must stay hidden.
  const isRestaurantOnly = sessionStorage.getItem('propertType') === 'R';

  // Send To Room Modal States
  const [sendToRoomModalOpen, setSendToRoomModalOpen] = useState(false);
  const [currentOrderForRoom, setCurrentOrderForRoom] = useState(null);
  const [roomNo, setRoomNo] = useState('');
  // Occupied rooms (active bookings) for the Send-to-Room dropdown, so the user
  // PICKS a real booked room instead of typing a number.
  const [activeRooms, setActiveRooms] = useState([]);

  // Change Table Modal States — transfer an order to another table.
  const [changeTableOpen, setChangeTableOpen] = useState(false);
  const [orderForTableChange, setOrderForTableChange] = useState(null);
  const [changeTableTarget, setChangeTableTarget] = useState('');
  const [tableOptions, setTableOptions] = useState([]);
  // For moving a room-service order to a different ROOM.
  const [changeRoomTarget, setChangeRoomTarget] = useState('');

  const [customerName, setCustomerName] = useState('');
  const [customerMobile, setCustomerMobile] = useState('');
  const [customerAddress, setCustomerAddress] = useState('');
  const [billToCompany, setBillToCompany] = useState(false);
  const [custCompanyName, setCustCompanyName] = useState('');
  const [custGstNo, setCustGstNo] = useState('');
  const [custCompanyAddress, setCustCompanyAddress] = useState('');
  const [discountAmount, setDiscountAmount] = useState('');
  const [discountIsPercent, setDiscountIsPercent] = useState(false);
  const [discountRemark, setDiscountRemark] = useState('');

  const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');

  // ── FIXED: All permission booleans now use checkPerm(P.CONSTANT, 'LITERAL_STRING') ──
  // Even if P.* constant value is wrong, the literal string will match correctly.
  const canAccessPage = checkPerm(P.PAGE_TABLE_ORDERS, 'PAGE_TABLE_ORDERS');
  const canViewOrders = checkPerm(P.TABLE_ORDER_VIEW, 'TABLE_ORDER_VIEW');
  const canCreateOrder = checkPerm(P.TABLE_ORDER_CREATE, 'TABLE_ORDER_CREATE');
  const canApplyDiscount = checkPerm(
    P.TABLE_ORDER_DISCOUNT,
    'TABLE_ORDER_DISCOUNT',
  );
  const canMergeTables =
    checkPerm(P.TABLE_ORDER_MERGE_TABLE, 'TABLE_ORDER_MERGE_TABLE') ||
    checkPerm(P.TABLE_ORDER_MERGE_TABLES, 'TABLE_ORDER_MERGE_TABLES');
  const canSplitBill = checkPerm(
    P.TABLE_ORDER_SPLIT_BILL,
    'TABLE_ORDER_SPLIT_BILL',
  );
  const canMarkPaid = checkPerm(
    P.TABLE_ORDER_MARK_PAID,
    'TABLE_ORDER_MARK_PAID',
  );
  const canPrintBill = checkPerm(
    P.TABLE_ORDER_PRINT_BILL,
    'TABLE_ORDER_PRINT_BILL',
  );

  const Companycurrency = company?.currency ?? '₹';
  const token = sessionStorage.getItem('token');

  const denyAction = useCallback(
    (message = 'You do not have permission to perform this action.') => {
      toast.error(message);
    },
    [],
  );

  const requirePerm = useCallback(
    (code, literalCode) => {
      const literal = literalCode || code;
      if (!checkPerm(code, literal)) {
        denyAction();
        return false;
      }
      return true;
    },
    [checkPerm, denyAction],
  );

  // Fetch Table Orders
  const fetchTableOrders = async () => {
    if (!company?.id) {
      setError('Company not found. Please login again.');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetTableOrders/${company.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (!res.ok) throw new Error('Failed to fetch table orders');

      const data = await res.json();
      console.log('Table Orders Data:', data);

      // Debug log — helps verify permission matching
      console.log(
        '[RBAC DEBUG] permissionCodes from session:',
        getStoredPermissions(),
      );
      console.log('[RBAC DEBUG] canViewOrders:', canViewOrders);
      console.log('[RBAC DEBUG] canAccessPage:', canAccessPage);

      const sortedData = Array.isArray(data)
        ? [...data].sort((a, b) => {
            if (a.isPaid === false && b.isPaid === true) return -1;
            if (a.isPaid === true && b.isPaid === false) return 1;
            return new Date(b.createDate) - new Date(a.createDate);
          })
        : [];

      setOrders(sortedData);
    } catch (err) {
      setError(err.message || 'Failed to fetch table orders');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ── FIXED: Only block if RBAC is active AND BOTH page + view perms are missing ──
    if (useGranularRbac && !canAccessPage) return;
    fetchTableOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Filter logic
  const filteredOrders = orders.filter((o) => {
    const nameMatch = (o?.firstName || o?.customerName || '')
      .toLowerCase()
      .includes(searchText.toLowerCase());

    const tableMatch =
      !tableFilter || (o?.tableno || '').toString().includes(tableFilter);

    const orderDate = new Date(o.createDate || o.createdate);
    const fromMatch = !fromDate || orderDate >= new Date(fromDate);
    const toMatch = !toDate || orderDate <= new Date(toDate + 'T23:59:59');

    // Room order = linked to a booking / has a room number; otherwise dine-in.
    const isRoomOrder =
      (o?.bookingId || 0) > 0 ||
      !!(o?.roomNumbers || o?.roomNumber || '').toString().trim();
    const typeMatch =
      typeFilter === 'All' ||
      (typeFilter === 'Room' ? isRoomOrder : !isRoomOrder);

    return nameMatch && tableMatch && fromMatch && toMatch && typeMatch;
  });

  const totalOrders = filteredOrders.length;
  const totalRevenue = filteredOrders.reduce(
    (sum, o) => sum + (o.finalPrice || 0),
    0,
  );

  const stats = [
    { title: 'Total Table Orders', value: totalOrders, color: 'text.primary' },
    {
      title: 'Total Revenue',
      value: `${Companycurrency} ${totalRevenue.toFixed(2)}`,
      color: 'success.main',
    },
    {
      title: 'Pending Invoices',
      value: filteredOrders.filter((o) => !o.isInvoice).length,
      color: 'warning.main',
    },
    {
      title: 'Avg. Order Value',
      value: totalOrders > 0 ? (totalRevenue / totalOrders).toFixed(0) : 0,
      color: 'text.primary',
    },
  ];

  const exportToExcel = () => {
    if (!requirePerm(P.TABLE_ORDER_VIEW, 'TABLE_ORDER_VIEW')) return;
    if (filteredOrders.length === 0) return;

    const rows = filteredOrders.map((o) => ({
      OrderID: `ORD_${o.id}`,
      BookingID: o.bookingId || '—',
      Customer: o.firstName || '—',
      Email: o.email || '—',
      Table: o.tableno || '—',
      Date: formatDate(o.createDate),
      Price: o.price || 0,
      GST: o.gstAmount || 0,
      FinalAmount: o.finalPrice || 0,
    }));

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [
        Object.keys(rows[0]).join(','),
        ...rows.map((r) => Object.values(r).join(',')),
      ].join('\n');

    const link = document.createElement('a');
    link.href = encodeURI(csvContent);
    link.download = `table_orders_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  const handleViewDetail = async (order) => {
    // ── FIXED: Use literal string so permission check is reliable ──
    if (!requirePerm(P.TABLE_ORDER_VIEW, 'TABLE_ORDER_VIEW')) return;
    setSelectedOrder(order);
    setOrderDetails([]);
    setDetailsDrawerOpen(true);

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GettableOrderDetails/${order.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await res.json();
      console.log('Order Detail:', data);
      setOrderDetails(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching order details:', err);
    }
  };

  const handleCloseDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setSelectedOrder(null);
    setOrderDetails([]);
  };

  const handleOpenManage = (order) => {
    if (!requirePerm(P.TABLE_ORDER_VIEW, 'TABLE_ORDER_VIEW')) return;
    setSelectedOrder(order);
    setManageOpen(true);
  };
  const handleCloseManage = () => {
    setManageOpen(false);
    setSelectedOrder(null);
  };

  // The order action buttons, rendered once and reused. `closeFn` closes
  // whichever surface invoked them before running the action. (Caller guards
  // that selectedOrder is set.)
  const renderOrderActions = (closeFn) => (
    <>
      {!selectedOrder.isPaid && (
        <>
          {canApplyDiscount && (
            <Button
              fullWidth
              variant='outlined'
              color='secondary'
              startIcon={<LocalOfferIcon />}
              onClick={() => {
                closeFn();
                openDiscountModal(selectedOrder);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Apply Discount
            </Button>
          )}
          {canCreateOrder && !isRestaurantOnly && (
            <Button
              fullWidth
              variant='outlined'
              color='info'
              onClick={() => {
                closeFn();
                handleOpenSendToRoomModal(selectedOrder);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Bill to Room
            </Button>
          )}
          {canCreateOrder && (
            <Button
              fullWidth
              variant='outlined'
              color='warning'
              onClick={() => {
                closeFn();
                handleOpenChangeTable(selectedOrder);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Change Table
            </Button>
          )}
          {canMarkPaid && (
            <Button
              fullWidth
              variant='contained'
              color='success'
              onClick={() => {
                closeFn();
                handleOpenPaymentModal(selectedOrder);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Mark as Paid
            </Button>
          )}
          {canSplitBill && (
            <Button
              fullWidth
              variant='outlined'
              color='info'
              startIcon={<CallSplitIcon />}
              onClick={() => {
                if (
                  !requirePerm(
                    P.TABLE_ORDER_SPLIT_BILL,
                    'TABLE_ORDER_SPLIT_BILL',
                  )
                )
                  return;
                closeFn();
                setOrderForSplit(selectedOrder);
                setSplitModalOpen(true);
              }}
              sx={{ textTransform: 'none', fontWeight: 600 }}>
              Split Bill
            </Button>
          )}
          <Button
            fullWidth
            variant='outlined'
            color='error'
            onClick={() => {
              const o = selectedOrder;
              closeFn();
              cancelOrder(o);
            }}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Cancel Order
          </Button>
        </>
      )}
      {canPrintBill && (
        <Button
          fullWidth
          variant='contained'
          color='primary'
          startIcon={<ReceiptIcon />}
          onClick={() => {
            closeFn();
            openPrintModal(selectedOrder);
          }}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Print Bill
        </Button>
      )}
      <Button
        fullWidth
        variant='outlined'
        startIcon={<PrintIcon />}
        onClick={() => {
          const o = selectedOrder;
          closeFn();
          reprintKot(o);
        }}
        sx={{ textTransform: 'none', fontWeight: 600 }}>
        Reprint KOT
      </Button>
      <Button
        fullWidth
        variant='outlined'
        color='primary'
        onClick={() => {
          const o = selectedOrder;
          closeFn();
          setLedgerFor({ orderId: o.id, bookingId: o.bookingId || 0 });
          setLedgerOpen(true);
        }}
        sx={{ textTransform: 'none', fontWeight: 600 }}>
        Payment Ledger
      </Button>
      <Button
        fullWidth
        variant='outlined'
        color='success'
        startIcon={<ReceiptIcon />}
        onClick={() => {
          const o = selectedOrder;
          closeFn();
          handleViewInvoice(o);
        }}
        sx={{ textTransform: 'none', fontWeight: 600 }}>
        View Invoice
      </Button>
    </>
  );

  const getPaymentStatusChip = (o) => {
    // Fully settled = paid flag OR nothing left pending on a priced order
    // (payment was taken even if the IsPaid flag wasn't flipped).
    const pending = Number(o.pendingAmount ?? o.finalPrice ?? 0);
    const settled = o.isPaid || (Number(o.finalPrice) > 0 && pending <= 0);
    return settled
      ? { label: 'Paid', color: 'success' }
      : { label: 'Unpaid', color: 'warning' };
  };

  const handleGenerateInvoice = async (order) => {
    if (!requirePerm(P.TABLE_ORDER_CREATE, 'TABLE_ORDER_CREATE')) return;
    try {
      await fetch(`${config.API_BASE_URL}/home/GenerateInvoice/${order.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTableOrders();
    } catch (err) {
      console.error('Invoice generation failed', err);
    }
  };

  const handleViewInvoice = (order) => {
    // No hash — the app uses BrowserRouter (e.g. /admin/dashboard), so the
    // "#/" form didn't match any route and showed a blank page. Matches the
    // hotel invoice pattern (/invoice/:id) and the room-order one (/RestroInvoice/:id).
    window.open(
      `${window.location.origin}/RestroInvoice/${order.id}`,
      '_blank',
    );
  };

  // ====================== PRINT BILL ======================
  const openPrintModal = (order) => {
    if (!requirePerm(P.TABLE_ORDER_PRINT_BILL, 'TABLE_ORDER_PRINT_BILL'))
      return;
    setCurrentOrderForAction(order);
    setCustomerName(order.customerName || order.firstName || '');
    setCustomerMobile(order.customerMobile || order.mobile || '');
    setCustomerAddress(order.customerAddress || order.address || '');
    setBillToCompany(!!(order.customerCompanyName || order.customerGstNo));
    setCustCompanyName(order.customerCompanyName || '');
    setCustGstNo(order.customerGstNo || '');
    setCustCompanyAddress(order.customerCompanyAddress || '');
    setPrintModalOpen(true);
  };

  const handleContinueAsGuest = () => {
    setCustomerName('Guest');
    setCustomerMobile('0000000000');
    setCustomerAddress('Guest Address');
  };

  const handlePrintBill = async () => {
    if (!requirePerm(P.TABLE_ORDER_PRINT_BILL, 'TABLE_ORDER_PRINT_BILL'))
      return;
    if (!currentOrderForAction) return;

    const customerData = {
      name: customerName || 'Guest',
      mobile: customerMobile || '0000000000',
      address: customerAddress || 'Guest Address',
      companyName: billToCompany ? custCompanyName : '',
      gst: billToCompany ? custGstNo : '',
      companyAddress: billToCompany ? custCompanyAddress : '',
    };

    // Persist the walk-in / counter customer on the order so the restaurant
    // invoice & guest reports can show who the bill was for. Non-fatal.
    try {
      await fetch(`${config.API_BASE_URL}/restaurantreport/ordercustomer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: currentOrderForAction.id,
          customerName: customerName.trim(),
          customerMobile: customerMobile.trim(),
          customerAddress: customerAddress.trim(),
          customerCompanyName: billToCompany ? custCompanyName.trim() : '',
          customerGstNo: billToCompany ? custGstNo.trim() : '',
          customerCompanyAddress: billToCompany ? custCompanyAddress.trim() : '',
        }),
      });
    } catch (e) {
      console.warn('Save order customer failed:', e);
    }

    await sendToPrinterAPI(currentOrderForAction, customerData);

    setPrintModalOpen(false);
    setCustomerName('');
    setCustomerMobile('');
    setCustomerAddress('');
    setBillToCompany(false);
    setCustCompanyName('');
    setCustGstNo('');
    setCustCompanyAddress('');
    fetchTableOrders();
  };

  const sendToPrinterAPI = async (order, customer) => {
    const machineId = window.location.hostname;
    const dateTime = new Date().toLocaleString('en-GB');

    let itemsForPrint = [];

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GettableOrderDetails/${order.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (res.ok) {
        const data = await res.json();
        console.log('Orderdetail data:', data);
        itemsForPrint = Array.isArray(data) ? data : [data];
      } else {
        console.error('Failed to fetch items');
      }
    } catch (err) {
      console.error('Error fetching items for print:', err);
    }

    if (itemsForPrint.length === 0) {
      toast.error('Items could not be loaded. Please try again.');
      return;
    }

    // ── 80mm thermal invoice: 48-col Font A, full paper width, GST layout. ──
    const W = 48;
    const rule = (ch) => ch.repeat(W) + '\n';
    const solid = rule('-');
    const dbl = rule('=');
    // Amount rows: label left, value right, across the full 48-col width.
    const amt = (n) => `${currency} ${Number(n || 0).toFixed(2)}`;
    const kv = (label, value, bold) => {
      const v = amt(value);
      const gap = Math.max(1, W - label.length - v.length);
      const s = label + ' '.repeat(gap) + v + '\n';
      return bold ? '\x1B\x45\x01' + s + '\x1B\x45\x00' : s;
    };
    const kvText = (label, value) => {
      const v = String(value);
      const gap = Math.max(1, W - label.length - v.length);
      return label + ' '.repeat(gap) + v + '\n';
    };

    // Item columns: Name 22 | Qty 5 | Rate 9 | Amount 12 = 48.
    const NAME_W = 22;
    const QTY_W = 5;
    const RATE_W = 9;
    const AMT_W = 12;
    const header =
      'Item'.padEnd(NAME_W) +
      'Qty'.padStart(QTY_W) +
      'Rate'.padStart(RATE_W) +
      'Amount'.padStart(AMT_W) +
      '\n';

    let totalQty = 0;
    const itemsText = itemsForPrint
      .map((item) => {
        const qty = Number(item.qty || 1);
        totalQty += qty;
        // Older rows sometimes store the line total in `price`; derive the unit
        // from the authoritative line total when available.
        const apiTotal = Number(item.totalPrice || item.finalPrice || 0);
        const apiUnit = Number(item.price || 0);
        const totalPrice = apiTotal > 0 ? apiTotal : apiUnit * qty;
        const unitPrice =
          totalPrice > 0 && qty > 0 ? totalPrice / qty : apiUnit;

        const fullName = String(item.name || 'Item').trim();
        let line =
          fullName.slice(0, NAME_W).padEnd(NAME_W) +
          String(qty).padStart(QTY_W) +
          unitPrice.toFixed(2).padStart(RATE_W) +
          totalPrice.toFixed(2).padStart(AMT_W);
        // Wrap the name onto its own lines only when it truly overflows.
        for (let i = NAME_W; i < fullName.length; i += NAME_W) {
          line += '\n' + fullName.slice(i, i + NAME_W);
        }
        return line;
      })
      .join('\n');

    const totalItems = itemsForPrint.length;
    const grandTotal = Number(order.finalPrice || 0);
    const roundedTotal = Math.round(grandTotal);
    const roundOff = Number((roundedTotal - grandTotal).toFixed(2));

    const vatItems = itemsForPrint.filter(
      (item) => item.isvataplicable === true || item.isvataplicable === 'true',
    );
    const gstItems = itemsForPrint.filter(
      (item) =>
        !(item.isvataplicable === true || item.isvataplicable === 'true'),
    );

    // Tax comes from the ORDER HEADER, not the item rows. AddDiscount
    // recomputes the header's VAT/GST on the DISCOUNTED total and never touches
    // the OrderDetail rows, so summing rows printed the PRE-discount tax next to
    // a POST-discount Total (a 900 order less 200 printed Total 700 with 900's
    // GST). Rows stay as the fallback for legacy orders whose header tax was
    // never written.
    const lineVatAmount = vatItems.reduce((sum, item) => {
      return (
        sum + Number(item.gstAmount || item.vatAmount || item.vataMount || 0)
      );
    }, 0);
    const headerVatAmount = Number(order.vataMount || order.vatAmount || 0);
    const totalVatAmount =
      headerVatAmount > 0 ? headerVatAmount : lineVatAmount;

    const vatRate =
      vatItems.length > 0
        ? Number(vatItems[0].vatrate || order.vatrate || 0)
        : Number(order.vatrate || 0);

    const totalCgst = gstItems.reduce((sum, item) => {
      return sum + Number(item.cgst || item.cGst || 0);
    }, 0);
    const totalSgst = gstItems.reduce((sum, item) => {
      return sum + Number(item.sgst || item.sGst || 0);
    }, 0);

    // Header first — see the VAT note above. The row sums are PRE-discount, so
    // preferring them printed a 900-GST beside a 700 Total.
    const headerCgst = Number(order.cGst || 0);
    const headerSgst = Number(order.sGst || 0);
    const finalCgst = headerCgst > 0 ? headerCgst : totalCgst;
    const finalSgst = headerSgst > 0 ? headerSgst : totalSgst;
    const finalGst = finalCgst + finalSgst;

    // ── Professional GST-inclusive flow ──
    // Gross -> Discount -> Grand Total (net incl.) -> Taxable -> CGST/SGST -> Payable
    const discount = Number(order.dicountamount || 0);
    const grossAmount = grandTotal + discount;
    const gstTaxable =
      Number(order.price || 0) > 0
        ? Number(order.price)
        : Number((grandTotal - finalGst).toFixed(2));
    const vatTaxable = Number((grandTotal - totalVatAmount).toFixed(2));
    // CGST/SGST half-rate derived from the stored amounts, never hardcoded.
    const halfRate = gstTaxable > 0 ? (finalGst / gstTaxable) * 50 : 0;
    const rl = (r) => (r > 0 ? ` ${Math.round(r * 100) / 100}%` : '');

    let totals = solid;
    totals += kv('Gross Amount', grossAmount);
    if (discount > 0) totals += kv('Discount (-)', discount);
    totals += solid;
    totals += kv('Grand Total', grandTotal, true);
    if (vatItems.length > 0) {
      totals += 'VAT Included\n';
      totals += kv('Taxable Amount', vatTaxable);
      totals += kv(`VAT${rl(vatRate)}`, totalVatAmount);
      if (gstItems.length > 0 && finalGst > 0) {
        totals += kv('CGST' + rl(halfRate), finalCgst);
        totals += kv('SGST' + rl(halfRate), finalSgst);
        totals += kv('Total GST', finalGst);
      }
    } else {
      totals += 'GST Included\n';
      totals += kv('Taxable Amount', gstTaxable);
      totals += kv('CGST' + rl(halfRate), finalCgst);
      totals += kv('SGST' + rl(halfRate), finalSgst);
      totals += kv('Total GST', finalGst);
    }
    if (roundOff !== 0) totals += kv('Round Off', roundOff);
    totals += dbl;
    totals += kv('PAYABLE AMOUNT', roundedTotal, true);
    totals += dbl;

    // Logo (raster, wider for 80mm) + optional UPI QR.
    const logo = await buildLogoEscPos(company?.logo || company?.Logo, 512);
    let upiQr = '';
    if (company?.upiId) {
      const upi = `upi://pay?pa=${encodeURIComponent(company.upiId)}&pn=${encodeURIComponent(company?.name || 'Restaurant')}&am=${encodeURIComponent(roundedTotal)}&cu=INR`;
      try {
        upiQr =
          '\x1B\x61\x01' +
          buildUpiQrEscPos(upi) +
          'Scan & Pay (UPI)\n' +
          '\x1B\x61\x00' +
          solid;
      } catch (e) {
        upiQr = '';
      }
    }

    // Optional fields — printed only when the data exists.
    const fssai = company?.fssai || company?.fssaiNo || company?.Fssai || '';
    const orderType = order.orderType || order.ordertype || '';
    const waiter = order.waiter || order.userName || order.createdByName || '';
    const gstinNo = company?.gstNo || company?.gstno || company?.gst || '';

    const data =
      '\x1B\x40' +
      logo +
      '\x1B\x61\x01' +
      '\x1B\x45\x01' +
      (company?.name || 'HOTEL') +
      '\n' +
      '\x1B\x45\x00' +
      (company?.address1 ? company.address1 + '\n' : '') +
      (gstinNo ? 'GSTIN: ' + gstinNo + '\n' : '') +
      (fssai ? 'FSSAI: ' + fssai + '\n' : '') +
      (company?.mobile ? 'Mob: ' + company.mobile + '\n' : '') +
      solid +
      '\x1B\x45\x01' +
      'TAX INVOICE\n' +
      '\x1B\x45\x00' +
      solid +
      '\x1B\x61\x00' +
      kvText('Invoice :', order.invNumber || '-') +
      kvText('Date    :', dateTime) +
      kvText('Table   :', order.tableno || '-') +
      (orderType ? kvText('Type    :', orderType) : '') +
      (waiter ? kvText('Waiter  :', waiter) : '') +
      kvText('Customer:', customer.name || '-') +
      kvText('Mobile  :', customer.mobile || '-') +
      (customer.companyName
        ? kvText('Company :', customer.companyName) +
          kvText('Cust GST:', customer.gst || '-')
        : '') +
      solid +
      header +
      solid +
      itemsText +
      '\n' +
      solid +
      kvText('Total Items :', totalItems) +
      kvText('Total Qty   :', totalQty) +
      totals +
      upiQr +
      '\x1B\x61\x01' +
      (order.paymentMode ? 'Payment Mode: ' + order.paymentMode + '\n' : '') +
      (waiter ? 'Cashier: ' + waiter + '\n' : '') +
      '\n' +
      '\x1B\x45\x01' +
      'THANK YOU, VISIT AGAIN!\n' +
      '\x1B\x45\x00' +
      'Software by GoERP.live\n' +
      '\n\n\n\n' +
      '\x1D\x56\x00';

    // #3: resolve the BILL printer up-front. It used to silently default to a
    // printer named 'Kitchen' (which does not exist as a bill printer), so every
    // invoice was dispatched into a black hole while we toasted success.
    const billPrinterName = resolveBillPrinter();
    if (!billPrinterName) {
      toast.error(
        'No bill printer configured. Map a department as the bill printer (or set the machine bill printer) and try again.',
      );
      return;
    }
    try {
      const res = await fetch(`${config.API_BASE_URL}/Service/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: 'BILL',
          content: data,
          machineId: machineId,
          companyid: company?.id,
          orderId: order.id,
          customerName: customer.name,
          customerMobile: customer.mobile,
          printerName: billPrinterName,
        }),
      });
      // fetch does NOT reject on 4xx/5xx — the old code swallowed the API's
      // 'No print machine registered' error and reported success anyway.
      if (!res.ok) {
        const msg = await res.text().catch(() => '');
        console.error('Bill print failed:', res.status, msg);
        toast.error('Bill print failed: ' + (msg || res.status));
        return;
      }
      toast.success('Bill sent to ' + billPrinterName);
    } catch (err) {
      console.error('Print API error:', err);
      toast.error('Failed to send bill to printer');
    }
  };

  // ====================== REPRINT KOT ======================
  // Lets the user re-send the kitchen ticket for an existing order (e.g. the
  // original KOT didn't print). Pulls the order's items and routes one KOT to
  // the Kitchen printer. (Per-department routing isn't possible here because
  // GettableOrderDetails doesn't carry the item's department.)
  const reprintKot = async (order) => {
    if (!order) return;
    const machineId = window.location.hostname;

    let items = [];
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/GettableOrderDetails/${order.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.ok) {
        const data = await res.json();
        items = Array.isArray(data) ? data : [data];
      }
    } catch (err) {
      console.error('Reprint KOT: fetch items failed', err);
    }

    if (items.length === 0) {
      toast.error('Items could not be loaded. Please try again.');
      return;
    }

    const W = 48;
    const solid = '-'.repeat(W) + '\n';
    const dbl = '='.repeat(W) + '\n';
    const ITEM_W = 42;
    const QTY_W = 6;
    const now = new Date();
    const date = now.toLocaleDateString('en-GB');
    const time = now.toLocaleTimeString();
    // A table order is dine-in (matches the original KOT's default TYPE).
    const type = order.tableno ? 'DINE-IN' : 'PARCEL';

    // Group items by department + KOT number so the reprint reproduces the
    // ORIGINAL order-screen KOT exactly: one ticket per department per KOT,
    // each showing its KOT number and routed to that department's printer.
    const groups = {};
    items.forEach((it) => {
      const dept =
        !it.department || String(it.department).trim() === ''
          ? 'General'
          : it.department;
      const kotno = Number(it.kotno || 0);
      const key = `${dept}||${kotno}`;
      (groups[key] = groups[key] || { dept, kotno, list: [] }).list.push(it);
    });

    let allOk = true;
    for (const key of Object.keys(groups)) {
      const { dept, kotno, list } = groups[key];
      // Centralised: Department → mapped printer (fallback handled inside).
      const printerName = resolveKotPrinter(dept);
      const itemsText = list
        .map((it) => {
          const display = it.subcategoryName
            ? `${it.subcategoryName} ${it.name || ''}`
            : it.name || 'Item';
          const full = String(display).trim();
          const q = String(Number(it.qty || 1)).padStart(QTY_W);
          let ln = full.slice(0, ITEM_W).padEnd(ITEM_W) + q;
          for (let i = ITEM_W; i < full.length; i += ITEM_W) {
            ln += '\n' + full.slice(i, i + ITEM_W);
          }
          return ln;
        })
        .join('\n');
      const totalKotQty = list.reduce((s, it) => s + Number(it.qty || 1), 0);

      let data = '\x1B\x40\x1B\x61\x01\x1B\x45\x01';
      data += (company?.name || 'HOTEL') + '\n\x1B\x45\x00';
      if (company?.address1) data += company.address1 + '\n';
      data += dbl + '\x1B\x45\x01KITCHEN ORDER TICKET\n\x1B\x45\x00' + dbl;
      data += '\x1B\x61\x00';
      // KOT number (fall back to order id for legacy rows) + routing meta.
      data += `KOT #: ${kotno || order.id}   ${type}\n`;
      data += `Dept : ${dept}\n`;
      data += `Table: ${order.tableno || '-'}\n`;
      data += `Date : ${date}  ${time}\n`;
      data += solid;
      data +=
        '\x1B\x45\x01' +
        'ITEM'.padEnd(ITEM_W) +
        'QTY'.padStart(QTY_W) +
        '\n\x1B\x45\x00';
      data += solid;
      // Item lines double-height + bold so the kitchen reads them at a glance.
      data +=
        '\x1D\x21\x01\x1B\x45\x01' +
        itemsText +
        '\n' +
        '\x1D\x21\x00\x1B\x45\x00';
      data += solid;
      data += 'Total Qty'.padEnd(ITEM_W) + String(totalKotQty).padStart(QTY_W) + '\n';
      data += dbl;
      data += '\n\n\n\x1D\x56\x00';

      try {
        const res = await fetch(`${config.API_BASE_URL}/Service/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            type: 'KOT',
            content: data,
            machineId,
            companyid: company?.id,
            printerName,
          }),
        });
        if (!res.ok) {
          const m = await res.text().catch(() => '');
          console.error('Reprint KOT failed:', res.status, m);
          allOk = false;
        }
      } catch (err) {
        console.error('Reprint KOT error:', err);
        allOk = false;
      }
    }

    if (allOk) toast.success('KOT reprinted to kitchen.');
    else
      toast.error('KOT reprint failed. Check printer / machine registration.');
  };

  // ====================== CANCEL ORDER ======================
  // Cancels the whole order (only when not yet paid). Backend marks it
  // cancelled, clears it from kitchen, and voids its ledger.
  const cancelOrder = async (order) => {
    if (!order) return;
    // Ask for a cancellation reason (required) so every cancel is accountable.
    const reason = window.prompt(
      `Cancel entire order ORD_${order.id}?\nEnter a reason for cancellation:`,
      '',
    );
    if (reason === null) return; // user pressed Cancel on the prompt
    if (!reason.trim()) {
      toast.error('A cancellation reason is required.');
      return;
    }
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/cancel`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: order.id,
            companyId: company?.id,
            reason: reason.trim(),
          }),
        },
      );
      if (!res.ok) {
        const m = await res.text().catch(() => '');
        toast.error(m || 'Could not cancel order.');
        return;
      }
      toast.success('Order cancelled.');
      fetchTableOrders();
    } catch (err) {
      console.error('Cancel order error:', err);
      toast.error('Failed to cancel order.');
    }
  };

  // ====================== ADD DISCOUNT ======================
  const openDiscountModal = (order) => {
    if (!requirePerm(P.TABLE_ORDER_DISCOUNT, 'TABLE_ORDER_DISCOUNT')) return;
    setCurrentOrderForAction(order);
    setDiscountAmount('');
    setDiscountIsPercent(false);
    setDiscountRemark('');
    setDiscountModalOpen(true);
  };

  const handleSaveDiscount = async () => {
    if (!requirePerm(P.TABLE_ORDER_DISCOUNT, 'TABLE_ORDER_DISCOUNT')) return;
    if (!currentOrderForAction || !discountAmount) {
      toast.error('Please enter discount amount');
      return;
    }
    if (discountIsPercent && Number(discountAmount) > 100) {
      toast.error('Percentage discount cannot be more than 100%');
      return;
    }

    // Never write with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    try {
      await fetch(`${config.API_BASE_URL}/home/AddDiscountForOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: currentOrderForAction.id,
          bookingId: currentOrderForAction.bookingId,
          discountAmount: parseFloat(discountAmount),
          isPercentage: discountIsPercent,
          remark: discountRemark.trim(),
          Userid: uid,
        }),
      });

      toast.success('Discount applied successfully!');
      setDiscountModalOpen(false);
      fetchTableOrders();
    } catch (err) {
      console.error('Discount API error:', err);
      toast.error('Failed to apply discount');
    }
  };

  // ====================== PAYMENT MODAL ======================
  // Opens the shared ProcessPaymentDialog (pending amount + multiple/split
  // payments). The actual save lives in that component.
  const handleOpenPaymentModal = (order) => {
    if (!requirePerm(P.TABLE_ORDER_MARK_PAID, 'TABLE_ORDER_MARK_PAID')) return;
    setCurrentOrderForPayment(order);
    setPaymentModalOpen(true);
  };

  // ====================== SEND TO ROOM ======================
  const handleOpenSendToRoomModal = async (order) => {
    if (!requirePerm(P.TABLE_ORDER_CREATE, 'TABLE_ORDER_CREATE')) return;
    setCurrentOrderForRoom(order);
    setRoomNo('');
    setSendToRoomModalOpen(true);
    // Load currently-booked (active) rooms so the user selects one.
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallactivebookings/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await res.json();
      const list = Array.isArray(data) ? data : [];
      // Flatten each active booking's rooms into { room, label } options.
      const rooms = [];
      list.forEach((b) => {
        const guest = `${b.firstName || ''} ${b.lastName || ''}`.trim();
        const details = Array.isArray(b.bookingRoomDetails)
          ? b.bookingRoomDetails
          : [];
        details.forEach((rd) => {
          const rn = rd.roomnumber ?? rd.roomNumber;
          if (rn != null && rn !== '') {
            rooms.push({
              room: String(rn),
              label: guest ? `Room ${rn} — ${guest}` : `Room ${rn}`,
            });
          }
        });
      });
      // De-dupe by room number.
      const seen = new Set();
      setActiveRooms(
        rooms.filter((r) => (seen.has(r.room) ? false : seen.add(r.room))),
      );
    } catch {
      setActiveRooms([]);
    }
  };

  const handleSendToRoom = async () => {
    if (!requirePerm(P.TABLE_ORDER_CREATE, 'TABLE_ORDER_CREATE')) return;
    if (!currentOrderForRoom) return;
    if (!roomNo.trim()) {
      toast.error('Please enter room number');
      return;
    }

    // Never write with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    try {
      await fetch(`${config.API_BASE_URL}/home/SendToRoom`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          orderId: currentOrderForRoom.id,
          roomNumber: roomNo,
          companyId: company?.id,
          userid: uid,
        }),
      });

      toast.success('Order sent to room successfully!');
      setSendToRoomModalOpen(false);
      setRoomNo('');
      fetchTableOrders();
    } catch (err) {
      console.error('Send to Room API error:', err);
      toast.error('Failed to send order to room');
    }
  };

  // ====================== CHANGE TABLE ======================
  const handleOpenChangeTable = async (order) => {
    if (!requirePerm(P.TABLE_ORDER_CREATE, 'TABLE_ORDER_CREATE')) return;
    setOrderForTableChange(order);
    setChangeTableTarget('');
    setChangeTableOpen(true);
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/tables/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const list = await res.json();
      setTableOptions(Array.isArray(list) ? list : []);
    } catch {
      setTableOptions([]);
    }
  };

  const handleSubmitChangeTable = async () => {
    if (!orderForTableChange) return;
    if (!changeTableTarget) {
      toast.error('Please select a table');
      return;
    }
    // Never write with userid 0; recover the session first.
    const uid = requireUserId();
    if (!uid) return;

    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/changetable`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: orderForTableChange.id,
            newTableNo: changeTableTarget,
            companyId: company?.id,
            changedBy: uid,
            changedByName:
              JSON.parse(sessionStorage.getItem('user') || '{}')?.name || '',
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || body?.message || 'Failed to change table');
        return;
      }
      toast.success(body?.message || 'Order moved successfully!');
      setChangeTableOpen(false);
      setOrderForTableChange(null);
      setChangeTableTarget('');
      fetchTableOrders();
    } catch (err) {
      console.error('Change Table API error:', err);
      toast.error('Failed to change table');
    }
  };

  // Move a room-service order to a different ROOM (the room must have an active
  // booking — the backend resolves it).
  const handleSubmitChangeRoomOrder = async () => {
    if (!orderForTableChange) return;
    const roomNo = Number(changeRoomTarget);
    if (!roomNo || roomNo <= 0) {
      toast.error('Enter a valid room number');
      return;
    }
    const uid = requireUserId();
    if (!uid) return;
    try {
      const res = await fetch(
        `${config.API_BASE_URL}/restaurantorder/changeroomorder`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            orderId: orderForTableChange.id,
            newRoomNo: roomNo,
            companyId: company?.id,
            changedBy: uid,
            changedByName:
              JSON.parse(sessionStorage.getItem('user') || '{}')?.name || '',
          }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.success === false) {
        toast.error(body?.error || body?.message || 'Failed to move order');
        return;
      }
      toast.success(body?.message || 'Order moved to room!');
      setChangeTableOpen(false);
      setOrderForTableChange(null);
      setChangeTableTarget('');
      setChangeRoomTarget('');
      fetchTableOrders();
    } catch (err) {
      console.error('Change Room order API error:', err);
      toast.error('Failed to move order');
    }
  };

  const getCurrency = (symbol) => {
    switch (symbol) {
      case '₹':
        return 'Rs.';
      case '$':
        return '$';
      case '€':
        return 'EUR';
      default:
        return symbol;
    }
  };
  const currency = getCurrency(Companycurrency);

  const clearFilters = () => {
    setSearchText('');
    setTableFilter('');
    setFromDate('');
    setToDate('');
  };

  // ── FIXED: Page access check — only block if RBAC active AND page perm missing ──
  if (useGranularRbac && !canAccessPage) {
    return (
      <Box
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: '#f8fafc',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 480,
            textAlign: 'center',
            borderRadius: 2,
          }}>
          <Typography
            variant='h6'
            fontWeight={700}
            color='primary'
            gutterBottom>
            Access restricted
          </Typography>
          <Typography color='text.secondary'>
            You do not have permission to open Table Orders.
          </Typography>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.25, sm: 2, md: 3 },
        bgcolor: '#f8fafc',
        minHeight: '100vh',
        overflowX: 'hidden',
        maxWidth: '100%',
      }}>
      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        mb={{ xs: 2, sm: 3 }}
        flexWrap='wrap'
        gap={1}>
        <Typography
          variant='h5'
          fontWeight={700}
          color='primary'
          sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}>
          Table Order Management
        </Typography>
        <Box display='flex' gap={1}>
          {canMergeTables && (
            <Button
              variant='outlined'
              size='small'
              color='secondary'
              startIcon={<MergeTypeIcon />}
              onClick={() => setMergeModalOpen(true)}>
              Merge Tables
            </Button>
          )}
          <Button
            variant='outlined'
            size='small'
            startIcon={<RefreshIcon />}
            onClick={fetchTableOrders}
            disabled={loading}>
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity='error' sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={2.5} mb={4}>
        {stats.map((stat, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card elevation={2} sx={{ height: '100%' }}>
              <CardContent sx={{ py: 2.5, px: 3 }}>
                <Typography color='text.secondary' variant='body2' gutterBottom>
                  {stat.title}
                </Typography>
                <Typography
                  variant='h4'
                  fontWeight={600}
                  color={stat.color}
                  sx={{ fontSize: '1.75rem' }}>
                  {stat.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: { xs: 1.5, sm: 3 }, mb: { xs: 2, sm: 3 } }}>
        {/* Order type tabs — All | Dine-In | Room. Auto-selected from the
            ?type= URL param after an order is placed (Room vs Dine-In). */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
          {[
            { key: 'Dinein', label: 'Dine-In' },
            { key: 'Room', label: 'Room' },
          ].map((t) => {
            const active = typeFilter === t.key;
            return (
              <Button
                key={t.key}
                size='small'
                onClick={() => setTypeFilter(t.key)}
                variant={active ? 'contained' : 'outlined'}
                sx={{
                  borderRadius: 20,
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.2,
                }}>
                {t.label}
              </Button>
            );
          })}
        </Box>
        <Box
          display='flex'
          gap={{ xs: 1, sm: 2 }}
          flexWrap='wrap'
          alignItems={{ xs: 'stretch', sm: 'end' }}>
          <TextField
            label='Search Customer'
            size='small'
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 200 } }}
          />
          <TextField
            label='Table Number'
            size='small'
            value={tableFilter}
            onChange={(e) => setTableFilter(e.target.value)}
            sx={{ minWidth: { xs: '100%', sm: 160 } }}
          />
          <TextField
            type='date'
            label='From Date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 'unset' } }}
          />
          <TextField
            type='date'
            label='To Date'
            size='small'
            InputLabelProps={{ shrink: true }}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            sx={{ minWidth: { xs: 'calc(50% - 4px)', sm: 'unset' } }}
          />
          {canViewOrders && (
            <Button
              variant='contained'
              size='small'
              onClick={exportToExcel}
              startIcon={<DownloadIcon />}>
              Export CSV
            </Button>
          )}
          <Button
            variant='text'
            color='inherit'
            size='small'
            onClick={clearFilters}
            sx={{ flex: { xs: 1, sm: 'initial' } }}>
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Main Table */}
      {useGranularRbac && !canViewOrders ? (
        <Paper
          elevation={3}
          sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color='text.secondary'>
            You do not have permission to view table orders.
          </Typography>
        </Paper>
      ) : (
        <>
        {/* Desktop / tablet: full table (hidden on phones where it's unreadable) */}
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: 'hidden',
            display: { xs: 'none', md: 'block' },
          }}>
          <TableContainer sx={{ overflowX: 'hidden' }}>
            <Table
              size='small'
              sx={{
                width: '100%',
                tableLayout: 'fixed',
                '& th, & td': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.main' }}>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      width: '11%',
                      textAlign: 'center',
                    }}>
                    Order ID
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, width: '22%' }}>
                    Customer
                  </TableCell>
                  <TableCell
                    sx={{
                      color: 'white',
                      fontWeight: 600,
                      width: '10%',
                      textAlign: 'center',
                    }}>
                    Table
                  </TableCell>
                  <TableCell
                    sx={{ color: 'white', fontWeight: 600, width: '18%' }}>
                    Time
                  </TableCell>
                  <TableCell
                    align='right'
                    sx={{ color: 'white', fontWeight: 600, width: '14%' }}>
                    Amount
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ color: 'white', fontWeight: 600, width: '12%' }}>
                    Status
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ color: 'white', fontWeight: 600, width: '13%' }}>
                    Details
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 10 }}>
                      <CircularProgress size={45} thickness={4.5} />
                      <Typography
                        variant='body1'
                        sx={{ mt: 2.5, color: 'text.secondary' }}>
                        Loading table orders...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredOrders.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align='center' sx={{ py: 12 }}>
                      <Typography
                        variant='h6'
                        color='text.secondary'
                        gutterBottom>
                        No orders found
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        No table orders match your current filters
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOrders.map((o) => {
                    const status = getPaymentStatusChip(o);
                    return (
                      <TableRow key={o.id} hover>
                        <TableCell
                          sx={{ fontWeight: 600, textAlign: 'center' }}>
                          ORD_{o.id}
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' fontWeight={500} noWrap>
                            {o.firstName ||
                              o.customerName ||
                              'Walk-in Customer'}
                          </Typography>
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={
                              (o.roomNumbers || o.roomNumber)
                                ? `Room ${o.roomNumbers || o.roomNumber}`
                                : o.tableno && o.tableno !== '*'
                                ? o.tableno
                                : o.bookingId ? `#${o.bookingId}` : '—'
                            }
                            size='small'
                            color='primary'
                            variant='filled'
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' noWrap>
                            {formatDate(o.createDate)}
                          </Typography>
                        </TableCell>
                        <TableCell align='right'>
                          <Typography
                            variant='body2'
                            fontWeight={700}
                            color='success.main'
                            noWrap>
                            {Companycurrency}{' '}
                            {Number(o.finalPrice || 0).toFixed(2)}
                          </Typography>
                          {Number(o.pendingAmount ?? 0) > 0 && (
                            <Typography
                              variant='caption'
                              color='warning.main'
                              noWrap
                              sx={{ display: 'block', fontWeight: 700 }}>
                              Pending: {Companycurrency}{' '}
                              {Number(o.pendingAmount).toFixed(2)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align='center'>
                          <Chip
                            label={status.label}
                            color={status.color}
                            size='small'
                          />
                        </TableCell>
                        <TableCell align='center'>
                          {/* ── FIXED: canViewOrders now uses checkPerm with literal string ── */}
                          {canViewOrders ? (
                            <Box
                              sx={{
                                display: 'flex',
                                gap: 0.5,
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                              }}>
                              <Button
                                size='small'
                                variant='outlined'
                                startIcon={
                                  <VisibilityIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() => handleViewDetail(o)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  px: 1,
                                  minWidth: 0,
                                  whiteSpace: 'nowrap',
                                }}>
                                View More
                              </Button>
                              <Button
                                size='small'
                                variant='contained'
                                color='primary'
                                onClick={() => handleOpenManage(o)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  px: 1,
                                  minWidth: 0,
                                  whiteSpace: 'nowrap',
                                  boxShadow: 'none',
                                }}>
                                Manage
                              </Button>
                              <Button
                                size='small'
                                variant='outlined'
                                color='success'
                                startIcon={
                                  <ReceiptIcon sx={{ fontSize: 16 }} />
                                }
                                onClick={() => handleViewInvoice(o)}
                                sx={{
                                  textTransform: 'none',
                                  fontWeight: 600,
                                  fontSize: 11,
                                  px: 1,
                                  minWidth: 0,
                                  whiteSpace: 'nowrap',
                                }}>
                                Invoice
                              </Button>
                            </Box>
                          ) : (
                            <Typography
                              variant='caption'
                              color='text.secondary'>
                              —
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Phones: each order as a readable card (the table columns crush on a
            narrow screen). Shown only on xs; desktop keeps the table above. */}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            flexDirection: 'column',
            gap: 1.25,
          }}>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <CircularProgress size={36} />
            </Box>
          ) : filteredOrders.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography color='text.secondary'>No orders found</Typography>
            </Paper>
          ) : (
            filteredOrders.map((o) => {
              const status = getPaymentStatusChip(o);
              return (
                <Paper
                  key={o.id}
                  elevation={2}
                  sx={{ borderRadius: 2, p: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.75,
                    }}>
                    <Typography fontWeight={700} fontSize={14}>
                      ORD_{o.id}
                    </Typography>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size='small'
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5,
                      gap: 1,
                    }}>
                    <Typography
                      fontSize={13}
                      fontWeight={500}
                      noWrap
                      sx={{ flex: 1 }}>
                      {o.firstName || o.customerName || 'Walk-in Customer'}
                    </Typography>
                    <Chip
                      label={
                        (o.roomNumbers || o.roomNumber)
                          ? `Room ${o.roomNumbers || o.roomNumber}`
                          : o.tableno && o.tableno !== '*'
                          ? o.tableno
                          : o.bookingId ? `#${o.bookingId}` : '—'
                      }
                      size='small'
                      color='primary'
                      variant='filled'
                    />
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}>
                    <Typography fontSize={12} color='text.secondary'>
                      {formatDate(o.createDate)}
                    </Typography>
                    <Typography
                      fontSize={15}
                      fontWeight={800}
                      color='success.main'>
                      {Companycurrency} {Number(o.finalPrice || 0).toFixed(2)}
                      {Number(o.pendingAmount ?? 0) > 0 && (
                        <Box component='span' sx={{ display: 'block', fontSize: 12, color: 'warning.main', fontWeight: 700 }}>
                          Pending: {Companycurrency} {Number(o.pendingAmount).toFixed(2)}
                        </Box>
                      )}
                    </Typography>
                  </Box>
                  {canViewOrders && (
                    <>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          fullWidth
                          size='small'
                          variant='outlined'
                          startIcon={<VisibilityIcon sx={{ fontSize: 16 }} />}
                          onClick={() => handleViewDetail(o)}
                          sx={{ textTransform: 'none', fontWeight: 600 }}>
                          View
                        </Button>
                        <Button
                          fullWidth
                          size='small'
                          variant='contained'
                          onClick={() => handleOpenManage(o)}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            boxShadow: 'none',
                          }}>
                          Manage
                        </Button>
                      </Box>
                      <Button
                        fullWidth
                        size='small'
                        variant='outlined'
                        color='success'
                        startIcon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                        onClick={() => handleViewInvoice(o)}
                        sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}>
                        View Invoice
                      </Button>
                    </>
                  )}
                </Paper>
              );
            })
          )}
        </Box>
        </>
      )}

      {/* Print Bill Modal */}
      <Dialog
        open={printModalOpen}
        onClose={() => setPrintModalOpen(false)}
        fullWidth
        maxWidth='sm'
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: { xs: 0, sm: 3 }, boxShadow: 4 } }}>
        <DialogTitle
          sx={{
            pb: 1,
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}>
          <ReceiptIcon color='primary' sx={{ fontSize: 28 }} />
          <Typography variant='h6' fontWeight={600}>
            Print Bill - Customer Details
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 3 }}>
          <DialogContentText sx={{ mb: 3, color: 'text.secondary' }}>
            Please fill customer details for the bill. You can also print as
            Guest.
          </DialogContentText>

          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <TextField
                label='Customer Name'
                fullWidth
                variant='outlined'
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder='Enter customer name'
                InputProps={{
                  startAdornment: (
                    <PersonIcon sx={{ color: 'text.secondary', mr: 1 }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Mobile Number'
                fullWidth
                variant='outlined'
                type='tel'
                value={customerMobile}
                onChange={(e) => setCustomerMobile(e.target.value)}
                placeholder='Enter mobile number'
                InputProps={{
                  startAdornment: (
                    <Box
                      component='span'
                      sx={{ color: 'text.secondary', mr: 1 }}>
                      📱
                    </Box>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label='Address (Optional)'
                fullWidth
                variant='outlined'
                multiline
                rows={3}
                value={customerAddress}
                onChange={(e) => setCustomerAddress(e.target.value)}
                placeholder='Enter full address'
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox checked={billToCompany} onChange={(e) => setBillToCompany(e.target.checked)} />
                }
                label='Bill to Company (GST invoice in company name)'
              />
            </Grid>
            {billToCompany && (
              <>
                <Grid item xs={12}>
                  <TextField label='Company Name' fullWidth variant='outlined' value={custCompanyName} onChange={(e) => setCustCompanyName(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label='Company GST No' fullWidth variant='outlined' value={custGstNo} onChange={(e) => setCustGstNo(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField label='Company Address' fullWidth variant='outlined' multiline rows={2} value={custCompanyAddress} onChange={(e) => setCustCompanyAddress(e.target.value)} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>

        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2.5,
            borderTop: '1px solid #f0f0f0',
            gap: 1,
            flexWrap: 'wrap',
            '& > button': { flex: { xs: '1 1 100%', sm: '0 0 auto' } },
          }}>
          <Button
            onClick={handleContinueAsGuest}
            variant='outlined'
            startIcon={<PersonIcon />}
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            Continue as Guest
          </Button>
          <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'block' } }} />
          <Button
            onClick={() => setPrintModalOpen(false)}
            variant='text'
            color='inherit'
            sx={{ textTransform: 'none', borderRadius: 2 }}>
            Cancel
          </Button>
          {canPrintBill && (
            <Button
              onClick={handlePrintBill}
              variant='contained'
              color='primary'
              startIcon={<ReceiptIcon />}
              sx={{
                textTransform: 'none',
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
              }}>
              Submit & Print Bill
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Discount Modal */}
      <Dialog
        open={discountModalOpen}
        onClose={() => setDiscountModalOpen(false)}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Add Discount to Order</DialogTitle>
        <DialogContent>
          <TextField
            select
            label='Discount Type'
            fullWidth
            value={discountIsPercent ? 'percent' : 'flat'}
            onChange={(e) =>
              setDiscountIsPercent(e.target.value === 'percent')
            }
            sx={{ mt: 2 }}>
            <MenuItem value='flat'>Flat (₹)</MenuItem>
            <MenuItem value='percent'>Percentage (%)</MenuItem>
          </TextField>
          <TextField
            label={
              discountIsPercent ? 'Discount (%)' : 'Discount Amount (₹)'
            }
            type='number'
            fullWidth
            value={discountAmount}
            onChange={(e) => setDiscountAmount(e.target.value)}
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <Typography>{discountIsPercent ? '%' : '₹'}</Typography>
              ),
            }}
          />
          <TextField
            label='Remark (reason for discount)'
            fullWidth
            value={discountRemark}
            onChange={(e) => setDiscountRemark(e.target.value)}
            sx={{ mt: 2 }}
            multiline
            minRows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountModalOpen(false)}>Cancel</Button>
          {canApplyDiscount && (
            <Button
              onClick={handleSaveDiscount}
              variant='contained'
              color='warning'>
              Save Discount
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Payment Modal */}
      <ProcessPaymentDialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        orderId={currentOrderForPayment?.id}
        orderLabel={
          currentOrderForPayment ? `ORD_${currentOrderForPayment.id}` : ''
        }
        pendingAmount={
          currentOrderForPayment?.pendingAmount ??
          currentOrderForPayment?.finalPrice ??
          0
        }
        currency={Companycurrency}
        onPaid={fetchTableOrders}
      />

      <PaymentLedgerModal
        open={ledgerOpen}
        onClose={() => setLedgerOpen(false)}
        orderId={ledgerFor.orderId}
        bookingId={ledgerFor.bookingId}
      />

      {/* Change Table Modal */}
      <Dialog
        open={changeTableOpen}
        onClose={() => setChangeTableOpen(false)}
        maxWidth='xs'
        fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Change Table / Room</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <DialogContentText sx={{ mb: 2 }}>
            Move order <b>#{orderForTableChange?.id}</b>
            {orderForTableChange?.tableno
              ? ` (currently ${orderForTableChange.tableno})`
              : ''}{' '}
            to another table, or to a room.
          </DialogContentText>
          <FormControl fullWidth>
            <InputLabel id='change-table-label'>New Table</InputLabel>
            <Select
              labelId='change-table-label'
              label='New Table'
              value={changeTableTarget}
              onChange={(e) => setChangeTableTarget(e.target.value)}>
              {tableOptions
                .filter(
                  (t) =>
                    String(t.tableName).trim() !==
                    String(orderForTableChange?.tableno || '').trim(),
                )
                .map((t) => (
                  <MenuItem key={t.id} value={t.tableName}>
                    {t.tableName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Typography
            sx={{
              textAlign: 'center',
              my: 1.5,
              color: 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: 700,
            }}>
            — OR move to a room —
          </Typography>
          <TextField
            fullWidth
            label='Room number'
            type='number'
            value={changeRoomTarget}
            onChange={(e) => setChangeRoomTarget(e.target.value)}
            helperText='Room must have an active booking.'
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={() => setChangeTableOpen(false)} color='inherit'>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitChangeRoomOrder}
            variant='outlined'
            color='primary'
            disabled={!changeRoomTarget}>
            Move to Room
          </Button>
          <Button
            onClick={handleSubmitChangeTable}
            variant='contained'
            color='warning'
            disabled={!changeTableTarget}>
            Move to Table
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send to Room Modal */}
      <Dialog
        open={sendToRoomModalOpen}
        onClose={() => {
          setSendToRoomModalOpen(false);
          setRoomNo('');
        }}
        maxWidth='xs'
        fullWidth
        fullScreen={isMobile}>
        <DialogTitle>Send Order to Room</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id='send-room-label'>Select Room (active bookings)</InputLabel>
            <Select
              labelId='send-room-label'
              label='Select Room (active bookings)'
              value={roomNo}
              onChange={(e) => setRoomNo(e.target.value)}>
              {activeRooms.length === 0 && (
                <MenuItem value='' disabled>
                  No active room bookings
                </MenuItem>
              )}
              {activeRooms.map((r) => (
                <MenuItem key={r.room} value={r.room}>
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSendToRoomModalOpen(false);
              setRoomNo('');
            }}>
            Cancel
          </Button>
          {canCreateOrder && (
            <Button
              onClick={handleSendToRoom}
              variant='contained'
              color='primary'>
              Send to Room
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Order Details Drawer */}
      <Drawer
        anchor='right'
        open={detailsDrawerOpen && !!selectedOrder}
        onClose={handleCloseDetailsDrawer}
        transitionDuration={280}
        sx={{ zIndex: ORDER_DRAWER_Z }}
        ModalProps={{ sx: { zIndex: ORDER_DRAWER_Z } }}
        slotProps={{
          backdrop: {
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.35)',
              backdropFilter: 'blur(2px)',
            },
          },
        }}
        PaperProps={{
          sx: {
            width: ORDER_DRAWER_WIDTH,
            maxWidth: '100vw',
            bgcolor: drawerTheme.bg,
            borderLeft: `1px solid ${drawerTheme.border}`,
            borderTopLeftRadius: '20px',
            borderBottomLeftRadius: '20px',
            boxShadow: '-8px 0 30px rgba(0, 0, 0, 0.15)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          },
        }}>
        {selectedOrder && (
          <>
            <Box
              sx={{
                flexShrink: 0,
                bgcolor: drawerTheme.primary,
                color: '#fff',
                px: 2,
                pt: 1.75,
                pb: 1.5,
                borderTopLeftRadius: '20px',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 1,
                }}>
                <Box minWidth={0}>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 16, color: '#fff' }}>
                    Order Details
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 11.5,
                      color: 'rgba(255,255,255,0.88)',
                      mt: 0.35,
                    }}>
                    ORD_{selectedOrder.id} · Table{' '}
                    {selectedOrder.tableno || '—'}
                  </Typography>
                </Box>
                <IconButton
                  onClick={handleCloseDetailsDrawer}
                  size='small'
                  sx={{
                    color: '#fff',
                    bgcolor: 'rgba(255,255,255,0.14)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.24)' },
                  }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>

            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                overflowX: 'hidden',
                px: 2,
                py: 1.75,
              }}>
              <DrawerSection title='Order Overview'>
                <DrawerRow
                  label='Customer'
                  value={
                    selectedOrder.firstName ||
                    selectedOrder.customerName ||
                    'Walk-in Customer'
                  }
                />
                {selectedOrder.email && (
                  <DrawerRow label='Email' value={selectedOrder.email} />
                )}
                {selectedOrder.mobile && (
                  <DrawerRow label='Mobile' value={selectedOrder.mobile} />
                )}
                <DrawerRow label='Table' value={selectedOrder.tableno || '—'} />
                <DrawerRow
                  label='Delivery'
                  value={
                    selectedOrder.isDeleverd === true ? 'Delivered' : 'Pending'
                  }
                />
                <DrawerRow
                  label='Date & Time'
                  value={formatDate(selectedOrder.createDate)}
                />
                <DrawerRow
                  label='Pending Amount'
                  value={`${Companycurrency} ${Number(selectedOrder.pendingAmount || 0).toFixed(2)}`}
                />
                {selectedOrder.bookingId && (
                  <DrawerRow
                    label='Booking ID'
                    value={selectedOrder.bookingId}
                  />
                )}
                <DrawerRow
                  label='Final Amount'
                  // The order header is the amount actually billed and printed —
                  // AddDiscount writes the discount there and never touches the
                  // item rows, so preferring the line sum showed this drawer the
                  // PRE-discount amount while the list and the bill showed the
                  // discounted one. Header first; fall back to the line sum only
                  // for legacy orders whose header was never totalled.
                  value={`${Companycurrency} ${(
                    Number(selectedOrder.finalPrice || 0) ||
                    (orderDetails || []).reduce(
                      (s, it) => s + Number(it.totalPrice || it.finalPrice || 0),
                      0,
                    )
                  ).toFixed(2)}`}
                  highlight
                  isLast
                />
              </DrawerSection>

              <DrawerSection title='Ordered Items'>
                {orderDetails.length === 0 ? (
                  <Box sx={{ p: 2, textAlign: 'center' }}>
                    <CircularProgress size={28} />
                  </Box>
                ) : (
                  orderDetails.map((item, i) => (
                    <Box
                      key={i}
                      sx={{
                        px: 1.75,
                        py: 1.1,
                        borderBottom:
                          i < orderDetails.length - 1
                            ? `1px solid ${drawerTheme.divider}`
                            : 'none',
                      }}>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 0.25,
                        }}>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 600 }}
                          noWrap>
                          {item.name}
                        </Typography>
                        <Typography
                          sx={{ fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                          {Companycurrency}{' '}
                          {Number(
                            item.totalPrice || item.finalPrice || 0,
                          ).toFixed(2)}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{ fontSize: 11, color: drawerTheme.textMuted }}>
                        Qty {item.qty}
                        {item.kotno ? ` · KOT ${item.kotno}` : ''}
                        {item.gstAmount
                          ? ` · GST ${Companycurrency}${Number(item.gstAmount).toFixed(2)}`
                          : ''}
                      </Typography>
                    </Box>
                  ))
                )}
              </DrawerSection>

              {/* Tax Summary — extracts CGST/SGST from the inclusive
                  finalPrice so the drawer never shows ₹0 even when the
                  backend didn't break GST out per-line. Sums per-line GST
                  first; falls back to total − total/(1+rate/100). */}
              {(() => {
                // Grand Total MUST equal what the bill prints, so this reads the
                // order header — the billed amount, and the only place a discount
                // is recorded. (It used to prefer the line sum, which meant a
                // discounted order showed its pre-discount total here while the
                // list and the printed bill showed the discounted one.) Legacy
                // orders with no header total still fall back to the line sum.
                const lineSum = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.totalPrice || it.finalPrice || 0),
                  0,
                );
                const total = Number(selectedOrder?.finalPrice || 0) || lineSum;
                if (total <= 0) return null;
                const rate = Number(company?.gstRate ?? company?.gstrate ?? 5);
                const perLineGst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.gstAmount || 0),
                  0,
                );
                const perLineCgst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.cgst || it.cGst || 0),
                  0,
                );
                const perLineSgst = (orderDetails || []).reduce(
                  (s, it) => s + Number(it.sgst || it.sGst || 0),
                  0,
                );
                // Header first, for the same reason the Total above uses it:
                // AddDiscount recomputes GST on the DISCOUNTED total and writes
                // it to the header only — the item rows keep their ORIGINAL tax.
                // Summing rows therefore showed 900's GST beside a 700 total.
                // Rows, then rate-extraction, remain the fallbacks for legacy
                // orders whose header tax was never written.
                const headerGst = Number(selectedOrder?.gstAmount || 0);
                const headerCgst = Number(selectedOrder?.cGst || 0);
                const headerSgst = Number(selectedOrder?.sGst || 0);
                const derivedGst =
                  headerGst > 0
                    ? headerGst
                    : perLineGst > 0
                      ? perLineGst
                      : Number((total - total / (1 + rate / 100)).toFixed(2));
                const derivedHalf = Number((derivedGst / 2).toFixed(2));
                const cgst =
                  headerCgst > 0
                    ? headerCgst
                    : perLineCgst > 0
                      ? perLineCgst
                      : derivedHalf;
                const sgst =
                  headerSgst > 0
                    ? headerSgst
                    : perLineSgst > 0
                      ? perLineSgst
                      : derivedHalf;
                const subtotalBase = Number((total - derivedGst).toFixed(2));
                return (
                  <DrawerSection title='Tax Summary'>
                    <DrawerRow
                      label='Subtotal (excl. GST)'
                      value={`${Companycurrency} ${subtotalBase.toFixed(2)}`}
                    />
                    <DrawerRow
                      label={`CGST (${(rate / 2).toFixed(2)}%)`}
                      value={`${Companycurrency} ${cgst.toFixed(2)}`}
                    />
                    <DrawerRow
                      label={`SGST (${(rate / 2).toFixed(2)}%)`}
                      value={`${Companycurrency} ${sgst.toFixed(2)}`}
                    />
                    <DrawerRow
                      label='GST Amount (incl.)'
                      value={`${Companycurrency} ${derivedGst.toFixed(2)}`}
                    />
                    <DrawerRow
                      label='Grand Total'
                      value={`${Companycurrency} ${total.toFixed(2)}`}
                      highlight
                      isLast
                    />
                  </DrawerSection>
                );
              })()}
            </Box>

            {/* Action buttons moved to the "Manage" dialog (opened from the
                orders table) — the details drawer is now detail-only. */}
          </>
        )}
      </Drawer>

      {/* Manage Order — actions dialog opened from the orders table. */}
      <Dialog
        open={manageOpen && !!selectedOrder}
        onClose={handleCloseManage}
        maxWidth='xs'
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
          Manage Order
          {selectedOrder ? ` — ORD_${selectedOrder.id}` : ''}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 2 }}>
          {selectedOrder && renderOrderActions(handleCloseManage)}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseManage}>Close</Button>
        </DialogActions>
      </Dialog>

      {canMergeTables && (
        <MergeTableModal
          open={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          orders={orders}
          token={token}
          company={company}
          currency={Companycurrency}
          onSuccess={() => {
            fetchTableOrders();
            setMergeModalOpen(false);
          }}
        />
      )}

      {canSplitBill && (
        <SplitBillModal
          open={splitModalOpen}
          onClose={() => {
            setSplitModalOpen(false);
            setOrderForSplit(null);
          }}
          order={orderForSplit}
          token={token}
          company={company}
          currency={Companycurrency}
          onSuccess={() => {
            fetchTableOrders();
          }}
        />
      )}
    </Box>
  );
}
